import type { ParsedQuery } from 'query-string'
import { useRef, useState } from 'react'

import { FvButton, LoadMore } from '@fv/client-components'
import { flattenPages } from '@fv/client-core'
import type { ShipmentListPreference } from '@fv/models'

import { ActiveShipmentsExportControls } from '@/components/ActiveShipmentsExportControls'
import { DividedContent } from '@/components/DividedContent'
import { Pinline } from '@/components/Pinline'
import { ScheduledReportsControls } from '@/components/ScheduledReportsControls'
import { ShipperSlider } from '@/components/ShipperSlider'
import { ShipperTooltip } from '@/components/ShipperTooltip'

import { CompactFilterBar } from '../shipment-filters/CompactFilterBar'
import { ShipmentFilterPanel } from '../shipment-filters/FilterPanel'
import { useHandleFilterChange } from '../shipment-filters/hooks'
import { BulkShipmentActions } from '../shipment-list/BulkShipmentActions'
import {
  isDefaultFilter,
  useActiveShipmentSelection,
  useConfirmedShipments,
} from '../shipment-list/hooks/useConfirmedShipments'
import {
  useConfirmedListParams,
  useSetActiveLoadId,
} from '../shipment-list/hooks/useShipmentListParams'
import { ShipmentViewSelect } from '../shipment-list/ShipmentViewSelect'
import { ColumnEditSlider } from './ColumnEditSlider'
import { ShipmentTable } from './ShipmentTable'

type Props = {
  onViewChange: (view: ShipmentListPreference) => void
}

const CondensedShipmentList = ({ onViewChange }: Props) => {
  const listRef = useRef<HTMLDivElement>(null)
  const shipmentsQuery = useConfirmedShipments()
  const shipments = flattenPages(shipmentsQuery.data?.pages)
  const { qs } = useConfirmedListParams(shipments)
  const setActiveLoadId = useSetActiveLoadId()
  const { handle: handleChange } = useHandleFilterChange(listRef)
  const { selected, setSelected, handleCheckAll, showBulkActions } =
    useActiveShipmentSelection(shipments)
  const [isColumnEditOpen, setColumnEditOpen] = useState(false)
  const [isScheduleOpen, setScheduleOpen] = useState(false)

  const handleFilterChange = (data: ParsedQuery) => {
    setSelected([])
    handleChange(data)
  }

  return (
    <div className="grid shipper-page-fill shipper-grid-rows-single-fill bg-fv-gray-200 overflow-hidden grid-cols-[19rem_1fr_0]">
      <ShipmentFilterPanel
        className="col-start-1 row-start-1"
        drawerAt="always"
        onChange={handleFilterChange}
        onScheduleOpen={() => setScheduleOpen(true)}
      />

      <div
        className="flex flex-col bg-fv-gray-100 overflow-auto py-8 px-6 relative z-10 col-span-full row-start-1"
        id="shipmentList"
        ref={listRef}
      >
        <nav className="mb-6 fa-width-auto flex items-center gap-x-2">
          <ShipmentViewSelect value="condensed" onChange={onViewChange} />
          <DividedContent className="flex-1">
            <div className="flex-1">
              <CompactFilterBar onChange={handleFilterChange} />
            </div>

            <ActiveShipmentsExportControls
              queryString={isDefaultFilter(qs) ? `isArchived=false&${qs}` : qs}
              buttonClassName="b1650:border-l-fv-gray b1650:border-l b1650:pl-4"
            />

            <div className="flex items-center gap-x-3">
              <Pinline className="w-4 border-fv-blue-300!" dashed />
              <ShipperTooltip label="Edit columns">
                <FvButton
                  icon="columns-3"
                  onClick={() => setColumnEditOpen(true)}
                />
              </ShipperTooltip>
            </div>
          </DividedContent>
        </nav>

        <div className="overflow-auto max-w-full pb-2">
          <ShipmentTable
            query={shipmentsQuery}
            onActivate={l => setActiveLoadId(l.loadId)}
            onToggleSelectAll={handleCheckAll}
            selected={selected}
            setSelected={setSelected}
          />
          <LoadMore
            className="mt-4"
            fetchNextPage={shipmentsQuery.fetchNextPage}
            hasNextPage={shipmentsQuery.hasNextPage}
            isLoading={shipmentsQuery.isFetchingNextPage}
          />
        </div>
      </div>

      {showBulkActions && (
        <BulkShipmentActions
          selected={selected}
          onDone={() => setSelected([])}
        />
      )}

      <ShipperSlider
        width="32rem"
        isOpen={isColumnEditOpen}
        onClose={() => setColumnEditOpen(false)}
      >
        {isColumnEditOpen && (
          <ColumnEditSlider closePanel={() => setColumnEditOpen(false)} />
        )}
      </ShipperSlider>

      <ScheduledReportsControls
        isOpen={isScheduleOpen}
        onClose={() => setScheduleOpen(false)}
      />
    </div>
  )
}

export default CondensedShipmentList
